/**
 * Каркас §57: sidebar + main workspace.
 * Контекстная правая панель появляется только при необходимости (фаза 9).
 *
 * Пока идёт коридор первого входа (§6, §71), разделов нет вовсе: страницы,
 * куда ведёт коридор, открываются в пустом каркасе с возвратом к шагам.
 * Иначе человек, нажав «создать образ», снова оказывался бы в кабинете
 * с восемью разделами — ровно там, откуда мы его уводим.
 */
import { Link, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { onboardingApi } from '../../api/onboarding';
import { useUiStore } from '../../stores/ui';
import { MobileNav } from './MobileNav';
import { Sidebar } from './Sidebar';
import { VerifyEmailBanner } from './VerifyEmailBanner';

export function AppLayout() {
  const { t } = useTranslation();
  const mobileNavOpen = useUiStore((state) => state.mobileNavOpen);
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen);

  const onboarding = useQuery({
    queryKey: ['onboarding'],
    queryFn: onboardingApi.get,
    staleTime: 10_000,
  });

  // Ошибку читаем как «коридор пройден»: сеть моргнула — человек не должен
  // остаться без навигации.
  const guiding = onboarding.data ? !onboarding.data.completed : false;

  if (guiding) {
    return (
      <div className="min-h-full bg-page">
        <main>
          <div className="mx-auto flex w-full max-w-[1360px] flex-col gap-6 px-5 py-8 lg:px-10 lg:py-10">
            <Link
              to="/start"
              className="flex items-center gap-2 self-start text-meta text-content-muted transition-colors duration-fast hover:text-content"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
              {t('start.backToSteps')}
            </Link>
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-page">
      <aside className="fixed inset-y-0 left-0 hidden w-sidebar lg:block">
        <Sidebar />
      </aside>

      {/* Мобильное меню §59 */}
      <div
        className={clsx(
          'fixed inset-0 z-40 lg:hidden',
          mobileNavOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        <div
          className={clsx(
            'absolute inset-0 bg-page/80 transition-opacity duration-slow',
            mobileNavOpen ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => setMobileNavOpen(false)}
        />
        <div
          className={clsx(
            'absolute inset-y-0 left-0 w-sidebar transition-transform duration-slow ease-out',
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <Sidebar onNavigate={() => setMobileNavOpen(false)} />
        </div>
      </div>

      <main className="pb-20 lg:pb-0 lg:pl-sidebar">
        <div className="mx-auto w-full max-w-[1360px] px-5 py-8 lg:px-10 lg:py-10">
          <VerifyEmailBanner />
          <Outlet />
        </div>
      </main>

      <MobileNav />
    </div>
  );
}
